import * as React from 'react'
import { useState } from 'react'
import {
  format,
  isAfter,
  isBefore,
  isSameDay,
  setMonth,
  setYear,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  startOfQuarter,
  endOfQuarter,
  subDays,
  subMonths,
  subQuarters,
  subYears,
} from 'date-fns'

import { monthKeys } from '@/modules/shared/constants/months'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { DatePickerRangeProps } from '../../types'
import { DateCalendar } from '@/ui/date/DateCalendar'
import { faChevronLeftSolid, faChevronRightSolid, FaIcon } from '@/lib/fa'



/** Submit
 * Callback function invoked when the user confirms the date selection.
 * Returns an object with `startDate` and `endDate` as strings or null .
 */

/** setPreviw
 * Optional callback invoked on every date click to provide a live preview of the selection.
 * Returns an object with `startDate` and `endDate` as strings.
 */

const YEARS_IN_WINDOW = 8

type Preset = {
  key: string
  getRange: (today: Date) => { from: Date; to: Date }
}

const PRESETS: Preset[] = [
  {
    key: 'CurrentMonth',
    getRange: (today) => ({ from: startOfMonth(today), to: endOfMonth(today) }),
  },
  {
    key: 'CurrentYear',
    getRange: (today) => ({ from: startOfYear(today), to: endOfYear(today) }),
  },
  {
    key: 'Last28Days',
    getRange: (today) => ({ from: subDays(today, 27), to: today }),
  },
  {
    key: 'Last30Days',
    getRange: (today) => ({ from: subDays(today, 29), to: today }),
  },
  {
    key: 'Last12Months',
    getRange: (today) => ({
      from: startOfMonth(subMonths(today, 11)),
      to: endOfMonth(today),
    }),
  },
  {
    key: 'PreviousMonth',
    getRange: (today) => {
      const previous = subMonths(today, 1)
      return { from: startOfMonth(previous), to: endOfMonth(previous) }
    },
  },
  {
    key: 'PreviousQuarter',
    getRange: (today) => {
      const previous = subQuarters(today, 1)
      return { from: startOfQuarter(previous), to: endOfQuarter(previous) }
    },
  },
  {
    key: 'PreviousYear',
    getRange: (today) => {
      const previous = subYears(today, 1)
      return { from: startOfYear(previous), to: endOfYear(previous) }
    },
  },
]

const toPickerDate = (date: Date | null) =>
  date
    ? {
        fullDate: format(date, 'DD-MM-YY'),
        day: date.getDate(),
        month: date.getMonth() + 1,
        year: date.getFullYear(),
      }
    : null

const fromPickerDate = (date: DatePickerRangeProps['startDate']) =>
  date ? new Date(date.year!, date.month! - 1, date.day!) : null

const windowStartFor = (year: number) => year - (YEARS_IN_WINDOW - 1)


const CALENDAR_COLUMN = 'tw-w-[15.5rem]'

const RangePanel = ({
  title,
  view,
  setView,
  yearWindowStart,
  setYearWindowStart,
  startDate,
  endDate,
  hoverDate,
  setHoverDate,
  onDayClick,
}: {
  title: string
  view: Date
  setView: (date: Date) => void
  yearWindowStart: number
  setYearWindowStart: (year: number) => void
  startDate: Date | null
  endDate: Date | null
  hoverDate: Date | null
  setHoverDate: (date: Date | null) => void
  onDayClick: (day: Date) => void
}) => {
  const { t } = useLocalize()

  const years = Array.from(
    { length: YEARS_IN_WINDOW },
    (_, i) => yearWindowStart + i,
  )

  return (
    <div className="tw-flex tw-shrink-0 tw-flex-col tw-px-3">
      <div className="tw-flex tw-items-center tw-justify-between tw-pb-3">
        <p className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-content-base/70">
          {title}
        </p>
        <div className="tw-flex tw-items-center tw-gap-3">
          <FaIcon
            icon={faChevronLeftSolid}
            className="tw-w-3 tw-cursor-pointer tw-text-content-base/70 hover:tw-text-primary"
            onClick={() =>
              setYearWindowStart(yearWindowStart - YEARS_IN_WINDOW)
            }
          />
          <FaIcon
            icon={faChevronRightSolid}
            className="tw-w-3 tw-cursor-pointer tw-text-content-base/70 hover:tw-text-primary"
            onClick={() =>
              setYearWindowStart(yearWindowStart + YEARS_IN_WINDOW)
            }
          />
        </div>
      </div>

      <div className="tw-flex tw-gap-3">
        <div className="tw-flex tw-w-[7.5rem] tw-shrink-0 tw-flex-col">
          {monthKeys.map((monthKey, index) => (
            <div
              key={monthKey}
              onClick={() => setView(setMonth(view, index))}
              className={`tw-cursor-pointer tw-rounded tw-px-2 tw-py-[3px] tw-text-xs tw-uppercase ${
                view.getMonth() === index
                  ? 'tw-bg-secondary tw-font-semibold tw-text-primary'
                  : 'tw-text-content-base/90 hover:tw-bg-base-300'
              }`}
            >
              {t(`${monthKey}`)}
            </div>
          ))}
        </div>

        <div
          className={`tw-flex tw-shrink-0 tw-flex-col tw-gap-3 ${CALENDAR_COLUMN}`}
        >
          <div className="tw-grid tw-grid-cols-4 tw-gap-1">
            {years.map((year) => (
              <div
                key={year}
                onClick={() => setView(setYear(view, year))}
                className={`tw-cursor-pointer tw-rounded tw-py-1 tw-text-center tw-text-xs ${
                  view.getFullYear() === year
                    ? 'tw-bg-secondary tw-font-semibold tw-text-primary'
                    : 'tw-text-content-base/90 hover:tw-bg-base-300'
                }`}
              >
                {year}
              </div>
            ))}
          </div>

          <DateCalendar
            compact
            currentMonth={view}
            startDate={startDate}
            endDate={endDate}
            type={'rangeDate'}
            hoverDate={hoverDate}
            handleDayClick={onDayClick}
            setHoverDate={setHoverDate}
          />
        </div>
      </div>
    </div>
  )
}

export const DatePickerRange = ({
  submit,
  setPreview,
  selectedDate,
}: {
  selectedDate: DatePickerRangeProps
  submit: (date: DatePickerRangeProps) => void
  setPreview?: React.Dispatch<DatePickerRangeProps>
  /** @deprecated nieużywane - rozmiar wynika z układu popupu */
  size?: 'sm' | 'md'
}) => {
  const { t } = useLocalize()

  const initialStart = fromPickerDate(selectedDate.startDate)
  const initialEnd = fromPickerDate(selectedDate.endDate)

  const [startDate, setStartDate] = useState<Date | null>(initialStart)
  const [endDate, setEndDate] = useState<Date | null>(initialEnd)
  const [hoverDate, setHoverDate] = useState<Date | null>(null)

  const [viewStart, setViewStart] = useState(initialStart || new Date())
  const [viewEnd, setViewEnd] = useState(initialEnd || new Date())
  const [yearWindowStart, setYearWindowStart] = useState(
    windowStartFor((initialStart || new Date()).getFullYear()),
  )
  const [yearWindowEnd, setYearWindowEnd] = useState(
    windowStartFor((initialEnd || new Date()).getFullYear()),
  )

  const moveView = (
    date: Date,
    setView: (value: Date) => void,
    windowStart: number,
    setWindowStart: (value: number) => void,
  ) => {
    setView(date)
    const year = date.getFullYear()
    if (year < windowStart || year > windowStart + YEARS_IN_WINDOW - 1) {
      setWindowStart(windowStartFor(year))
    }
  }

  const handleStartDayClick = (day: Date) => {
    if (startDate && isSameDay(day, startDate)) {
      setStartDate(null)
      return
    }
    setStartDate(day)
    if (endDate && isAfter(day, endDate)) {
      setEndDate(null)
    }
  }

  const handleEndDayClick = (day: Date) => {
    if (endDate && isSameDay(day, endDate)) {
      setEndDate(null)
      return
    }
    setEndDate(day)
    if (startDate && isBefore(day, startDate)) {
      setStartDate(null)
    }
  }

  const handlePresetClick = (preset: Preset) => {
    const { from, to } = preset.getRange(new Date())
    setStartDate(from)
    setEndDate(to)
    moveView(from, setViewStart, yearWindowStart, setYearWindowStart)
    moveView(to, setViewEnd, yearWindowEnd, setYearWindowEnd)
  }

  const handlesubmit = () => {
    submit({
      startDate: toPickerDate(startDate),
      endDate: toPickerDate(endDate),
    })
  }

  React.useEffect(() => {
    if (setPreview) {
      setPreview({
        startDate: toPickerDate(startDate),
        endDate: toPickerDate(endDate),
      })
    }
  }, [startDate, endDate])

  return (
    <div className="tw-z-100 tw-w-max tw-select-none tw-rounded-xl tw-bg-white tw-p-4 tw-font-sans tw-shadow">
      <div className="tw-flex tw-items-stretch">
        <div className="tw-w-[10.5rem] tw-shrink-0 tw-pr-3">
          <p className="tw-pb-3 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-content-base/70">
            {t('common.select')}
          </p>
          <div className="tw-flex tw-flex-col">
            {PRESETS.map((preset) => (
              <div
                key={preset.key}
                onClick={() => handlePresetClick(preset)}
                className="tw-cursor-pointer tw-rounded tw-px-2 tw-py-[5px] tw-text-xs tw-text-content-base/90 hover:tw-bg-base-300 hover:tw-text-primary"
              >
                {t(`dateRangeOption.${preset.key}`)}
              </div>
            ))}
          </div>
        </div>

        <div className="tw-w-px tw-shrink-0 tw-bg-neutral/15" />

        <RangePanel
          title={t('businessTrip.start')}
          view={viewStart}
          setView={(date) =>
            moveView(date, setViewStart, yearWindowStart, setYearWindowStart)
          }
          yearWindowStart={yearWindowStart}
          setYearWindowStart={setYearWindowStart}
          startDate={startDate}
          endDate={endDate}
          hoverDate={hoverDate}
          setHoverDate={setHoverDate}
          onDayClick={handleStartDayClick}
        />

        <div className="tw-w-px tw-shrink-0 tw-bg-neutral/15" />

        <RangePanel
          title={t('businessTrip.end')}
          view={viewEnd}
          setView={(date) =>
            moveView(date, setViewEnd, yearWindowEnd, setYearWindowEnd)
          }
          yearWindowStart={yearWindowEnd}
          setYearWindowStart={setYearWindowEnd}
          startDate={startDate}
          endDate={endDate}
          hoverDate={hoverDate}
          setHoverDate={setHoverDate}
          onDayClick={handleEndDayClick}
        />
      </div>

      <div className="tw-flex tw-items-center tw-justify-end tw-gap-4 tw-pt-4">
        {startDate && endDate && (
          <p className="tw-text-xs tw-text-content-base/70">
            {format(startDate, 'DD-MM-YYYY')} – {format(endDate, 'DD-MM-YYYY')}
          </p>
        )}
        <button
          onClick={handlesubmit}
          disabled={!startDate || !endDate}
          className={`tw-rounded-lg tw-px-4 tw-py-2 tw-text-sm tw-font-medium hover:tw-opacity-90 ${
            startDate && endDate
              ? 'tw-bg-primary tw-text-white'
              : 'tw-bg-content-base/20 tw-text-gray-400'
          }`}
        >
          {t('common.confirm')}
        </button>
      </div>
    </div>
  )
}
